import * as React from 'react';
import classNames from 'classnames';
import Highlight from 'react-highlighter';

import './styles.scss';

import { findDOMNode } from 'react-dom';
import { Popover, Position } from '@blueprintjs/core';
import { findIndex, isEqual } from 'lodash';

import Menu from '../Menu';
import MenuItem from '../MenuItem';
import ControlInput from '../ControlInput';
import { scrollTo } from '../../Utilities/ScrollTo';
import { getScrollParent, offsetTill } from '../../Utilities/Generic';
import Avatar from '../Avatar';
import LoadingContent from '../LoadingContent';
import ControlTagInput, { IControlTagInputProps } from '../ControlTagInput';
import Icon from '../Icon';

export interface IAutocompleteOption {
    label: string;
    value: string;
    subtext?: string;
    avatarUrl?: string | null;
    data?: any;
}

export interface IControlAutocompleteProps {
    /** placeholder */
    placeholder?: string;
    /** Controlled value */
    value: string;
    /** If values are provided, then ControlTagInput is used for multiple entry */
    values?: IControlTagInputProps['values'];
    /** Data to render in dropdown (gets highlighted with value) */
    data: IAutocompleteOption[];
    /** Change handler */
    onChange: (value) => void;
    /** Blur handler */
    onBlur?: () => void;
    /** Selected item from data */
    onSelect: (selectedOption: IAutocompleteOption) => void;
    /** Disabled status */
    disabled?: boolean;
    /** Readonly status */
    readonly?: boolean;
    /** Focus field on mount */
    autoFocus?: boolean;
    /** Color higlight for errors */
    hasError?: boolean;
    /** Minimal characters required to display results */
    minChars: number;
    /** Render avatar in the menu based on label */
    renderAvatar?: boolean;
    /** Addon element to prepend to field */
    addOnFront?: React.ReactElement;
    /** DOM target element name attribute */
    name?: string;
    /** DOM target element id attribute */
    id?: string;
    /** Loading */
    loading?: boolean;
    /** onRemove event for ControlTagInput */
    onRemove?: IControlTagInputProps['onRemove'];
    /** onAdd event for ControlTagInput */
    onAdd?: IControlTagInputProps['onAdd'];
    /** className to append */
    className?: string;
}

interface IState {
    hasFocus: boolean;
    isOpen: boolean;
    selectedIndex: number;
    selectedOption: any;
    tagInputValue: string;
}

/**
 * Uncontrolled autocomplete handler. Renders input with value inside and list of given results (highlighted with input value). Does not handle loading or filtering, you have to provide your own.
 */
export default class ControlAutocomplete extends React.Component<IControlAutocompleteProps, IState> {
    static displayName: string = 'ControlAutocomplete';

    private nodeWidthContainer;
    private nodeSearchField;
    private nodePopover;
    private preventClosing = false;

    constructor (props: IControlAutocompleteProps) {
        super(props);
        this.state = {
            hasFocus: false,
            isOpen: false,
            selectedIndex: -1,
            selectedOption: null,
            tagInputValue: '',
        };
    }

    handleClosePopover = () => {
        if (!this.preventClosing) {
            this.setState({
                isOpen: false,
            });
        }
    };

    handleKeypress = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.handleArrowDown(e);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.handleArrowUp(e);
        } else if (e.key === 'Escape') {
            this.handleEscape(e);
        } else if (e.key === 'Enter') {
            this.handleEnter(e);
        } else if (e.key === 'Tab') {
            this.handleTab(e);
        }
    };

    handleKeypressClosed = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
            e.preventDefault();
            this.setState({
                isOpen: this.shouldOpen(),
            });
        } else if (e.key === 'Escape') {
            this.handleEscape(e);
        } else if (e.key === 'Tab') {
            this.handleTab(e);
        }
    };

    handleArrowDown = (e) => {
        e.preventDefault();
        let newIndex = this.state.selectedIndex;
        if (this.props.data.length <= this.state.selectedIndex + 1) {
            newIndex = 0;
        } else {
            newIndex = this.state.selectedIndex + 1;
        }
        this.setState({
            selectedIndex: newIndex,
        });
    };

    handleArrowUp = (e) => {
        e.preventDefault();
        let newIndex = this.state.selectedIndex;
        if (this.state.selectedIndex - 1 < 0) {
            newIndex = this.props.data.length - 1;
        } else {
            newIndex = this.state.selectedIndex - 1;
        }
        this.setState({
            selectedIndex: newIndex,
        });
    };

    handleEscape = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: false,
        });
    };

    handleEnter = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: false,
        });
        if (
            this.state.selectedIndex !== -1 &&
            this.props.data.length > this.state.selectedIndex
        ) {
            // Set value selected with arrow keys
            this.returnValue(this.props.data[this.state.selectedIndex]);
        } else if (
            this.state.selectedIndex === -1 &&
            this.props.data.length > 0
        ) {
            // There is no value selected with arrow keys, select first
            this.returnValue(this.props.data[0]);
        }
    };

    handleTab = (e) => {
        if (this.nodeSearchField) {
            const shiftPressed = e.shiftKey;
            const allFocusables = document.querySelectorAll<HTMLElement>('button, A[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const currentIndex = findIndex(allFocusables, this.nodeSearchField);
            if (shiftPressed) {
                if (currentIndex !== -1 && allFocusables.length > currentIndex - 1) {
                    e.preventDefault();
                    allFocusables[currentIndex - 1].focus();
                }
            } else {
                if (currentIndex !== -1 && allFocusables.length > currentIndex + 1) {
                    e.preventDefault();
                    allFocusables[currentIndex + 1].focus();
                }
            }
        }
    };

    handleFocus = () => {
        this.setState({
            isOpen: this.shouldOpen(),
            hasFocus: true,
        });
    };

    handleBlur = () => {
        if (!this.preventClosing) {
            this.setState({
                hasFocus: false,
            });
            if (this.props.onBlur) {
                this.props.onBlur();
            }
            this.handleClosePopover();
        }
    };

    shouldOpen = () => {
        return (
            this.props.data.length > 0 &&
            this.props.value.length >= this.props.minChars &&
            !(
                this.props.data.length === 1 &&
                this.props.data[0].label.toLowerCase() === this.props.value.toLowerCase()
            )
        );
    };

    handleResize = () => {
        if (this.nodePopover && this.nodeSearchField && this.nodeWidthContainer) {
            const selectWidth = this.nodeWidthContainer.offsetWidth;
            this.nodePopover.style.maxWidth = selectWidth + 'px';
            this.nodePopover.style.width = '100%';
        }
    };

    returnValue = (value: IAutocompleteOption) => {
        this.props.onSelect(value);
        if (this.nodeSearchField) {
            this.nodeSearchField.focus();
        }
        this.setState({
            isOpen: false,
            tagInputValue: '',
        });
    };

    componentDidUpdate (prevProps: Readonly<IControlAutocompleteProps>, prevState: Readonly<IState>): void {
        if (!prevState.isOpen && this.state.isOpen) {
            this.setState({
                selectedIndex: -1,
                selectedOption: null,
            });
            document.addEventListener('keydown', this.handleKeypress);
            document.removeEventListener('keydown', this.handleKeypressClosed);
        }

        if (!this.state.isOpen && this.state.hasFocus) {
            document.addEventListener('keydown', this.handleKeypressClosed);
        }

        if (prevState.isOpen && !this.state.isOpen) {
            document.removeEventListener('keydown', this.handleKeypress);
        }

        if (!this.state.hasFocus && !this.state.isOpen) {
            document.removeEventListener('keydown', this.handleKeypress);
            document.removeEventListener('keydown', this.handleKeypressClosed);
        }

        // Handle dropdown display

        if (this.state.hasFocus && this.props.value !== prevProps.value) {
            if (!this.state.isOpen && this.props.data.length > 0 && this.props.value.length >= this.props.minChars) {
                // It makes sense to open
                this.setState({
                    isOpen: this.shouldOpen(),
                });
            } else if (this.state.isOpen && (this.props.data.length === 0 || this.props.value.length < this.props.minChars)) {
                // It makes sense to close
                this.setState({
                    isOpen: false,
                });
            }
        } else if (this.state.hasFocus) {
            if (!isEqual(prevProps.data, this.props.data)) {
                this.setState({
                    isOpen: this.shouldOpen(),
                });
            }
        }
    }

    componentWillUnmount (): void {
        document.removeEventListener('keydown', this.handleKeypress);
        document.removeEventListener('keydown', this.handleKeypressClosed);
        window.removeEventListener('resize', this.handleResize);
    }

    render () {
        const hasValue = this.props.value !== '';
        return (
            <Popover
                target={(
                    <div
                        className="control-autocomplete-inner"
                        ref={(node) => {
                            this.nodeWidthContainer = node;
                            this.handleResize();
                        }}
                    >
                        {!!this.props.values ? (
                            <ControlTagInput
                                placeholder={this.props.placeholder}
                                values={this.props.values}
                                onRemove={this.props.onRemove}
                                onAdd={this.props.onAdd}
                                inputProps={{
                                    value: this.state.tagInputValue,
                                    onChange: (e) => {
                                        this.setState({
                                            tagInputValue: e.target.value,
                                        });
                                        this.props.onChange(e.target.value);
                                    },
                                    onFocus: this.handleFocus,
                                    onBlur: this.handleBlur,
                                }}
                                disabled={this.props.disabled}
                                inputRef={(node) => {
                                    this.nodeSearchField = node;
                                    this.handleResize();
                                }}
                                addOnFront={this.props.addOnFront ? this.props.addOnFront : (
                                    <Icon width={16} height={16} kind="search"/>
                                )}
                            />
                        ) : (
                            <ControlInput
                                placeholder={this.props.placeholder}
                                autoFocus={this.props.autoFocus}
                                value={this.props.value}
                                onChange={(e) => {
                                    this.props.onChange(e.target.value);
                                }}
                                inputRef={(e) => {
                                    this.nodeSearchField = e;
                                    this.handleResize();
                                }}
                                onFocus={this.handleFocus}
                                onBlur={this.handleBlur}
                                addOn={this.props.loading ? (<LoadingContent layout="inline" />) : (this.props.value === '' ? undefined : (
                                    <button
                                        disabled={this.props.disabled}
                                        className="control-select-addon"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this.props.onChange('');
                                            if (this.props.onRemove) {
                                                this.props.onRemove(this.props.value, 0, this.props.value);
                                            }
                                            setTimeout(() => {
                                                if (this.nodeSearchField) {
                                                    this.nodeSearchField.focus();
                                                }
                                            }, 1);
                                        }}
                                        tabIndex={-1}
                                    >
                                        <Icon
                                            width={16}
                                            height={16}
                                            kind="clear"
                                        />
                                    </button>
                                ))}
                                addOnFront={this.props.addOnFront ? this.props.addOnFront : (
                                    <Icon width={16} height={16} kind="search"/>
                                )}
                                hasError={this.props.hasError}
                                disabled={this.props.disabled}
                                readonly={this.props.readonly}
                                name={this.props.name}
                                id={this.props.id}
                            />
                        )}
                    </div>
                )}
                content={(
                    <div
                        className={classNames('control-autocomplete-body', { open: this.state.isOpen })}
                        onMouseDown={() => {
                            this.preventClosing = true;
                        }}
                        onMouseUp={() => {
                            this.preventClosing = false;
                        }}
                    >
                        {this.state.isOpen && (
                            <Menu>
                                {this.props.data.map((child, index) => {
                                    return (
                                        <MenuItem
                                            key={index}
                                            text={(
                                                <div className="inner">
                                                    <div className="inner-text">
                                                        <div className="text">
                                                            {child.label === '' ? '\u00a0' : (
                                                                this.props.value !== '' && this.props.value.length >= 2 ? (
                                                                    <Highlight search={this.props.value}
                                                                               ignoreDiacritics={true}>{child.label}</Highlight>
                                                                ) : child.label
                                                            )}
                                                        </div>
                                                        {!!child.subtext && (
                                                            <div className="subtext">{child.subtext}</div>
                                                        )}
                                                    </div>
                                                    {this.props.renderAvatar && (
                                                        <Avatar
                                                            name={child.label}
                                                            size={40}
                                                            url={child.avatarUrl}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            disabled={this.props.disabled}
                                            onClick={() => {
                                                this.returnValue(child);
                                            }}
                                            className={classNames({
                                                'with-avatar': this.props.renderAvatar,
                                                'with-subtext': child.subtext,
                                                'active': child.value === this.props.value,
                                                'highlight': index === this.state.selectedIndex,
                                            })}
                                            innerRef={(elem) => {
                                                if (elem && index === this.state.selectedIndex) {
                                                    const domElem = findDOMNode(elem) as HTMLElement;
                                                    const domElemParent = getScrollParent(domElem) as HTMLElement;

                                                    if (!(domElemParent instanceof Window) && domElemParent) {
                                                        scrollTo(domElemParent, {
                                                            top: offsetTill(domElem, domElemParent) - (domElemParent.getBoundingClientRect().height / 2) + (domElem.getBoundingClientRect().height / 2),
                                                        }).then(() => {
                                                        });
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Menu>
                        )}
                    </div>
                )}
                autoFocus={false}
                openOnTargetFocus={false}
                hasBackdrop={false}
                isOpen={this.state.isOpen}
                canEscapeKeyClose={false}
                // onClose={this.handleClosePopover}
                portalClassName={classNames('control-autocomplete-portal', { 'control-autocomplete-portal-open': this.state.isOpen })}
                popoverClassName="control-autocomplete-popover"
                position={Position.BOTTOM_LEFT}
                minimal={true}
                usePortal={true}
                enforceFocus={false}
                modifiers={{
                    offset: {
                        offset: '0, 2px',
                        order: 201,
                    },
                    inner: {
                        enabled: false,
                    },
                    flip: {
                        enabled: false,
                    },
                    shift: {
                        enabled: false,
                    },
                    preventOverflow: {
                        enabled: false,
                    },
                    hide: {
                        enabled: false,
                    },
                }}
                wrapperTagName="div"
                className={classNames('control-autocomplete', {
                    'has-error': this.props.hasError,
                    'has-value': hasValue,
                    'disabled': this.props.disabled,
                }, this.props.className)}
                popoverRef={(e) => {
                    if (e) {
                        this.nodePopover = e;
                        window.addEventListener('resize', this.handleResize);
                        this.handleResize();
                    }
                }}
                transitionDuration={100}
            />
        );
    }
}
